import { cn } from '@/utils';
import { Icon } from '@/components/Icons';
import { TIconName } from '@/design-tokens';

interface ManagerTableLinkButtonProps {
  /** Additional CSS classes */
  className?: string;
  /** Optional icon name to display alongside the label */
  icon?: TIconName;
  /** Label for the button */
  label: string;
  /** Click handler for the button */
  onClick?: () => void;
}

/**
 * ManagerTableLinkButton Component
 *
 * A reusable button component designed for the Manager Dashboard.
 * It supports an optional icon, customizable label, and click handling.
 * The button also allows for an optional vertical offset to adjust its position.
 */
export const ManagerTableLinkButton: React.FC<ManagerTableLinkButtonProps> = ({
  className,
  icon,
  label = 'View',
  onClick,
  ...props
}) => {
  // Combine base styles with any additional classes passed via props
  const buttonClassNames = cn(
    'flex items-center gap-1',
    'text-neutral-gray-700 hover:text-brand-primary',
    'focus-visible:outline-1',
    'focus-visible:outline-brand-primary-700',
    'focus-visible:outline-solid',
    'focus-visible:outline-offset-2',
    className,
  );

  // Styles for the label, ensuring it inherits text color and has an underline
  const labelClassNames = cn(
    'pointer-events-none',
    'text-sm text-inherit',
    'underline underline-offset-2',
  );

  return (
    <button
      aria-label={label}
      className={buttonClassNames}
      onClick={onClick}
      type="button"
      {...props}
    >
      {icon && <Icon name={icon} size={16} weight="fill" />}
      <span className={labelClassNames}>{label}</span>
    </button>
  );
};

ManagerTableLinkButton.displayName = 'ManagerTableLinkButton';

export default ManagerTableLinkButton;
